
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import '../styles/History.css';
// Use API base from environment variable
const API_BASE = import.meta.env.VITE_API_BASE;

/* ── Icons ── */
const IconClock = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <circle cx="8" cy="8" r="6.5" />
    <polyline points="8,4 8,8 11,10" />
  </svg>
);

const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2.5,7.5 5.5,10.5 11.5,4" />
  </svg>
);

const IconFile = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
    <path d="M8 1H3.5A1.5 1.5 0 0 0 2 2.5v9A1.5 1.5 0 0 0 3.5 13h7a1.5 1.5 0 0 0 1.5-1.5V5L8 1z" />
    <polyline points="8,1 8,5 12,5" />
  </svg>
);

const IconEye = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
    <path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8s-2.5 4.5-6.5 4.5S1.5 8 1.5 8z" />
    <circle cx="8" cy="8" r="2" />
  </svg>
);

const IconRefresh = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <path d="M2 8a6 6 0 0 1 10.47-4M14 8a6 6 0 0 1-10.47 4" />
    <polyline points="12,1 13,4 10,4" />
    <polyline points="4,12 3,15 6,15" />
  </svg>
);

function History() {
  const navigate = useNavigate();
  const { apiFetch } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [validationDetail, setValidationDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const resp = await apiFetch(`${API_BASE}/outputs/browse`);
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`API error: ${resp.status} - ${text}`);
      }
      const data = await resp.json();
      const sorted = (data.folders || []).slice().sort((a, b) => {
        const ta = new Date(a.created_at || a.timestamp || 0).getTime();
        const tb = new Date(b.created_at || b.timestamp || 0).getTime();
        return tb - ta;
      });
      setDatasets(sorted);
    } catch (err) {
      console.error('Error loading history:', err);
      setDatasets([]);
    }
    setLoading(false);
  };

  const toggleValidation = async (folder) => {
    if (expandedFolder === folder) {
      setExpandedFolder(null);
      setValidationDetail(null);
      return;
    }
    setExpandedFolder(folder);
    setDetailLoading(true);
    setValidationDetail(null);
    try {
      const resp = await apiFetch(`${API_BASE}/outputs/validation/${encodeURIComponent(folder)}`);
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`API error: ${resp.status} - ${text}`);
      }
      setValidationDetail(await resp.json());
    } catch (err) {
      setValidationDetail(null);
      console.error('Error loading validation:', err);
    }
    setDetailLoading(false);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch { return ts; }
  };

  const getElapsed = (ts) => {
    if (!ts) return '—';
    // Approximate elapsed based on timestamps — in a real scenario this would come from job metadata
    return '< 1 min';
  };

  const getDatasetName = (ds) => {
    // Use metadata if available, otherwise clean folder name
    if (ds.dataset_name) return ds.dataset_name;
    const f = ds.folder;
    // Strip trailing _YYYYMMDD_HHMMSS timestamp suffix
    const stripped = f.replace(/_\d{8}_\d{6}$/, '');
    return stripped || 'Untitled dataset';
  };

  const getConfigName = (ds) => {
    if (ds.config_name) return ds.config_name;
    const f = ds.folder;
    const stripped = f.replace(/_\d{8}_\d{6}$/, '');
    return stripped || '—';
  };

  return (
    <div className="hist-page">
      {/* Hero */}
      <div className="hist-hero">
        <span className="hero-label">GENERATION HISTORY</span>
        <h1>Data Generation Runs</h1>
        <p>Browse all past generation runs — review datasets, configs used, and validation results.</p>
      </div>

      {/* Toolbar */}
      <div className="hist-toolbar">
        <div className="hist-toolbar-left">
          <span className="hist-count">{datasets.length} run{datasets.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="hist-btn" onClick={loadHistory}>
          <IconRefresh /> Refresh
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="hist-loading">Loading history…</div>
      ) : datasets.length === 0 ? (
        <div className="hist-empty">
          <IconClock />
          <h3>No generation runs yet</h3>
          <p>Go to the Pipeline to generate your first dataset.</p>
          <button className="hist-btn hist-btn-primary" onClick={() => navigate('/pipeline')}>
            Open Pipeline →
          </button>
        </div>
      ) : (
        <div className="hist-table-wrap">
          <table className="hist-table">
            <thead>
              <tr>
                <th>Dataset Name</th>
                <th>Date &amp; Time</th>
                <th>Config Used</th>
                <th>Elapsed</th>
                <th>Validation</th>
                <th>Files</th>
                <th>Records</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {datasets.map((ds, i) => (
                <React.Fragment key={ds.folder}>
                <tr className={i % 2 === 0 ? 'row-even' : ''}>
                  <td>
                    <span className="hist-dot" style={{ background: ['#6366f1','#0f766e','#b45309','#e11d48','#0284c7','#7c3aed','#059669','#475569'][i % 8] }} />
                    <span className="hist-dataset-name">{getDatasetName(ds)}</span>
                  </td>
                  <td>
                    <span className="hist-datetime">{formatDateTime(ds.created_at)}</span>
                  </td>
                  <td>
                    <span className="hist-config-badge">{getConfigName(ds)}</span>
                  </td>
                  <td>
                    <span className="hist-elapsed"><IconClock /> {getElapsed(ds.created_at)}</span>
                  </td>
                  <td>
                    {ds.validation ? (
                      <span
                        className={`hist-validation-badge ${ds.validation.valid ? 'pass' : 'fail'}`}
                        onClick={() => toggleValidation(ds.folder)}
                        title="Click to see details"
                      >
                        {ds.validation.valid
                          ? <><IconCheck /> Passed</>
                          : <>{ds.validation.error_count} error{ds.validation.error_count !== 1 ? 's' : ''}</>
                        }
                      </span>
                    ) : (
                      <span className="hist-validation-na">—</span>
                    )}
                  </td>
                  <td>
                    <span className="hist-files"><IconFile /> {ds.file_count}</span>
                  </td>
                  <td>
                    <span className="hist-records">
                      {ds.total_records ? ds.total_records.toLocaleString() : '—'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="hist-see-btn"
                      onClick={() => navigate(`/outputs?folder=${encodeURIComponent(ds.folder)}`)}
                    >
                      <IconEye /> See Data
                    </button>
                  </td>
                </tr>
                {expandedFolder === ds.folder && (
                  <tr className="hist-detail-row">
                    <td colSpan="8">
                      {detailLoading && <div className="hist-detail-loading">Loading validation details…</div>}
                      {!detailLoading && !validationDetail && (
                        <div className="hist-detail-empty">No validation results stored for this run.</div>
                      )}
                      {!detailLoading && validationDetail && (
                        <div className="hist-detail-panel">
                          <div className={`hist-detail-header ${validationDetail.valid ? 'pass' : 'fail'}`}>
                            {validationDetail.valid ? '✓ All Checks Passed' : '✗ Validation Failed'}
                          </div>
                          <div className="hist-detail-checks">
                            {Object.entries(validationDetail.checks || {}).map(([k, v]) => (
                              <div key={k} className={`hist-detail-check ${v.passed ? '' : 'failed'}`}>
                                <span className={`hist-detail-badge ${v.passed ? 'pass' : 'fail'}`}>
                                  {v.passed ? '✓' : '✗'}
                                </span>
                                <span className="hist-detail-name">{k.replace(/_/g, ' ')}</span>
                                {v.errors?.length > 0 && (
                                  <span className="hist-detail-err-count">
                                    {v.errors.length} error{v.errors.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                          {Object.entries(validationDetail.checks || {}).map(([k, v]) =>
                            v.errors?.length > 0 ? (
                              <div key={k} className="hist-detail-error-block">
                                <div className="hist-detail-error-title">{k.replace(/_/g, ' ')}</div>
                                {v.errors.slice(0, 20).map((e, i) => (
                                  <div key={i} className="hist-detail-error-line">• {e}</div>
                                ))}
                                {v.errors.length > 20 && (
                                  <div className="hist-detail-error-more">… and {v.errors.length - 20} more</div>
                                )}
                              </div>
                            ) : null
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default History;